import { useCallback, useRef, useState, type ReactNode } from 'react';

import {
  Badge,
  MAX_BADGES_DETAIL,
  PointsWithIcon,
  PointsWithIconVariant,
} from '@hungryroot/pho';
import { type PairingDetail } from '@hungryroot/rest';
import { useIdentify, AuthState } from '@hungryroot/rest-hooks';
import {
  EDIT_DELIVERY_GROCERIES_ROUTE,
  PRE_AUTH_CATALOG_GROCERIES_ROUTE,
} from '@hungryroot/routes';
import {
  Heading,
  Modal,
  Paragraph,
  TransparentButton,
  Information,
} from '@hungryroot/ui-kit';
import { type PdpHeroAvailabilityBadgeVariant } from '../../types/pdpHeroAvailabilityBadge';
import {
  ProductInfo,
  type ProductInfoLabelValue,
} from '../ProductInfo/ProductInfo';
import { RDPImageContent } from '../RDPImageContent/RDPImageContent';

import styles from './RDP.module.css';
export interface RDPProps {
  imageUrl?: string;
  title: string;
  discount?: number | null;
  discountPercentage?: number | null;
  pointsAfterDiscount?: number | null;
  credits?: number;
  badges?: string[];
  badgesOverride?: ReactNode;
  warningComponent?: ReactNode;
  productInfo: ProductInfoLabelValue[];
  packagedImageUrl?: string | null;
  primaryTopIconButton?: ReactNode;
  secondaryTopIconButton?: ReactNode;
  primaryTopRightIconButton?: ReactNode;
  ingredients?: PairingDetail['ingredients'];
  brandName?: string | null;
  brandId?: number | null;
  isEdited?: boolean;
  children: ReactNode;
  className?: string;
  header?: ReactNode;
  currentPoints?: number;
  ingredientOnSale?: boolean;
  onImageScroll?: (imageIndex?: number) => void;
  onProductContentScroll?: (scrollDepth: number) => void;
  heroImageAvailabilityBadge?: PdpHeroAvailabilityBadgeVariant | null;
}

const DISCOUNT_DISCLAIMER = `Discounted items are available while supplies last. Promotional pricing subject to change without notice. Rain checks not available. Discounts cannot be combined with other promotions unless explicitly stated. All sales on discounted items are final unless required by law.`;

/**
 * This is incorrectly named. It is not specific to only Recipes but a component used for both
 * Recipes and Products
 */
export function RDP(props: RDPProps) {
  const {
    imageUrl,
    title,
    credits,
    discount,
    discountPercentage,
    pointsAfterDiscount,
    badges,
    badgesOverride,
    warningComponent,
    productInfo,
    packagedImageUrl,
    primaryTopIconButton,
    secondaryTopIconButton,
    primaryTopRightIconButton,
    ingredients,
    isEdited,
    children,
    brandName,
    brandId,
    className,
    header,
    onImageScroll,
    currentPoints,
    ingredientOnSale,
    heroImageAvailabilityBadge,
  } = props;

  const badgesRef = useRef<HTMLDivElement>(null);
  const dragState = useRef({ isDown: false, startX: 0, scrollLeft: 0 });
  const [showFade, setShowFade] = useState(false);

  const updateFade = useCallback(() => {
    const el = badgesRef.current;
    if (!el) return;
    setShowFade(
      el.scrollWidth > el.clientWidth &&
        el.scrollLeft + el.clientWidth < el.scrollWidth - 1
    );
  }, []);

  const onBadgesPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const el = badgesRef.current;
      if (!el) return;
      el.setPointerCapture(e.pointerId);
      dragState.current = {
        isDown: true,
        startX: e.clientX,
        scrollLeft: el.scrollLeft,
      };
      el.classList.add(styles.dragging);
    },
    []
  );

  const onBadgesPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragState.current.isDown) return;
      e.preventDefault();
      const walk = e.clientX - dragState.current.startX;
      if (badgesRef.current) {
        badgesRef.current.scrollLeft = dragState.current.scrollLeft - walk;
      }
    },
    []
  );

  const onBadgesPointerUp = useCallback(() => {
    dragState.current.isDown = false;
    badgesRef.current?.classList.remove(styles.dragging);
  }, []);

  const { authState, customerData } = useIdentify();
  const isCheckedOut =
    authState === AuthState.isAuthenticated &&
    customerData?.did_checkout === true;
  const brandGroceriesRoute = isCheckedOut
    ? EDIT_DELIVERY_GROCERIES_ROUTE
    : PRE_AUTH_CATALOG_GROCERIES_ROUTE;
  const showDiscounts = discount && Number(discount) > 0;
  const [isDiscountInfoModalOpen, setIsDiscountInfoModalOpen] = useState(false);

  const isAIGenImage = imageUrl?.includes('ai_gen');
  const shouldHideAIImage = Boolean(
    authState === AuthState.isAuthenticated &&
      customerData?.ai_image_previews_opted_in === false &&
      isAIGenImage
  );

  // TODO need to change the nav bar to fixed on scroll state https://www.figma.com/file/UHhNpOxBhITAeCsrwh5fZa/PD-149-RDP%2FPDP-Redesign?node-id=1170%3A142002&mode=dev
  return (
    <div className={`${styles.container} ${className}`}>
      {header}
      <div className={styles['image-wrapper']}>
        <RDPImageContent
          imageUrl={imageUrl}
          packagedImageUrl={packagedImageUrl}
          ingredients={ingredients}
          shouldHideAIImage={shouldHideAIImage}
          onImageScroll={onImageScroll}
          heroImageAvailabilityBadge={heroImageAvailabilityBadge}
        />
      </div>
      {primaryTopIconButton && (
        <div className={styles['primary-top-icon-button']}>
          {primaryTopIconButton}
        </div>
      )}
      {secondaryTopIconButton && (
        <div className={styles['secondary-top-icon-button']}>
          {secondaryTopIconButton}
        </div>
      )}
      {primaryTopRightIconButton && (
        <div className={styles['primary-top-right-icon-button']}>
          {primaryTopRightIconButton}
        </div>
      )}
      <div className={styles['info-container']}>
        {warningComponent && (
          <div className={styles['warning-text-container']}>
            {warningComponent}
          </div>
        )}

        {brandName &&
          (brandId != null ? (
            <a
              href={`${brandGroceriesRoute}?brandId=${brandId}`}
              className={styles['brand-link']}
            >
              <Paragraph className={styles.brand}>{brandName}</Paragraph>
            </a>
          ) : (
            <Paragraph className={styles.brand}>{brandName}</Paragraph>
          ))}
        <Heading className={styles.header} variant="H3">
          {title}
          {isEdited && (
            <Paragraph className={styles.edited}>(edited)</Paragraph>
          )}
        </Heading>
        {credits && credits > 0 ? (
          <div className={styles['credits-container']}>
            <div className={styles.credits}>
              <PointsWithIcon
                testid="credits-total"
                points={credits.toString()}
                variant={PointsWithIconVariant.Large}
                pointsAfterDiscount={pointsAfterDiscount}
              />
              {isEdited && !ingredientOnSale && currentPoints && (
                <PointsWithIcon
                  testid="edited-credits-total"
                  points={currentPoints?.toString() ?? ''}
                  variant={PointsWithIconVariant.Removed}
                  pointsAfterDiscount={null}
                />
              )}
              {showDiscounts && (
                <>
                  <TransparentButton
                    className={styles['discount-info-button']}
                    onClick={() => setIsDiscountInfoModalOpen(true)}
                  >
                    <Information className={styles['discount-info-icon']} />
                  </TransparentButton>
                  <Modal
                    testid="discount-info-modal"
                    ariaLabel="Discount Information"
                    confirmButtonText="Got it"
                    onActionClick={() => {
                      setIsDiscountInfoModalOpen(false);
                    }}
                    isOpen={isDiscountInfoModalOpen}
                    onDismiss={() => {
                      setIsDiscountInfoModalOpen(false);
                    }}
                    header={<Heading variant="H2">Offer Details</Heading>}
                    body={
                      <div className={styles['discount-modal-body']}>
                        <Paragraph variant="body">
                          {DISCOUNT_DISCLAIMER}
                        </Paragraph>
                      </div>
                    }
                  ></Modal>
                </>
              )}
            </div>
            {Boolean(
              showDiscounts &&
                discountPercentage &&
                Number(discountPercentage) > 0
            ) && (
              <Badge
                variant="Sale"
                className={styles['discount-badge']}
                label={`${discountPercentage}% off`}
              />
            )}
          </div>
        ) : null}
        <div className={styles['product-info']}>
          <ProductInfo productInfo={productInfo} />
        </div>
        {badgesOverride
          ? badgesOverride
          : badges &&
            badges?.length > 0 && (
              <div className={styles['badges-wrapper']}>
                {showFade && <div className={styles['badges-fade']} />}
                <div
                  ref={(el) => {
                    (
                      badgesRef as React.MutableRefObject<HTMLDivElement | null>
                    ).current = el;
                    if (el) updateFade();
                  }}
                  className={styles.badges}
                  role="region"
                  aria-label="Product badges"
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- scrollable region needs keyboard focus
                  tabIndex={0}
                  onScroll={updateFade}
                  onPointerDown={onBadgesPointerDown}
                  onPointerMove={onBadgesPointerMove}
                  onPointerUp={onBadgesPointerUp}
                  onPointerCancel={onBadgesPointerUp}
                >
                  {badges.slice(0, MAX_BADGES_DETAIL).map((badge) => (
                    <Badge key={badge} label={badge} />
                  ))}
                </div>
              </div>
            )}
      </div>
      {children}
    </div>
  );
}
